// App.js
import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import "./App.css";
import samhead from "./samhead.jpg";
// import fetch from "node-fetch";

// CV/Resume data (single source for PDF and page)
const CV_DATA = {
  name: "Sam Yoong",
  title: "Full Stack Developer",
  tagline: "Specializing in Mobile Apps and Web Applications",
  about:
    "Full Stack Developer with experience in web and mobile application development using React.js, React Native, Node.js, REST APIs, and C# .NET MVC. Skilled in frontend UI and backend service development.",
  skills: [
    "HTML", "CSS", "JavaScript (ES6)", "React.js", "React Native", "jQuery",
    "Node.js", "REST API", "C# .NET MVC", "PHP", "Java"
  ],
  experience: [
    {
      title: "Full Stack Developer",
      company: "LHS Digital World Sdn Bhd",
      period: "Sep 2023 – Present",
      responsibilities: [
        "Developed full stack web applications using C# MVC (.NET Framework)",
        "Built and integrated service APIs",
        "Implemented UI using HTML, CSS, JavaScript, and jQuery"
      ]
    },
    {
      title: "Technician",
      company: "IMT Scientific Resources Sdn Bhd",
      period: "Feb 2023 – Sep 2023",
      responsibilities: [
        "Gas generator system servicing, installation, and troubleshooting"
      ]
    },
    {
      title: "Software Developer",
      company: "Techies App Technologies Sdn Bhd",
      period: "Feb 2022 – Dec 2022",
      responsibilities: [
        "React.js web development and Node.js REST APIs",
        "React Native mobile app development (Android & iOS)"
      ]
    }
  ],
  education: [
    {
      institution: "New Era Institute of Vocational",
      period: "Sep 2019 – Apr 2022",
      description: "Studied PHP, HTML, JavaScript, C#, Java, Photoshop"
    }
  ],
  contact: {
    email: "ypsamyoong@gmail.com",
    mobile: "+60 16-611 4889",
    location: "Selangor, Malaysia",
    website: "https://www.it-mixrice.org"
  }
};

// PDF layout: resume header photo size in mm (A4 page is 210mm wide)
const PDF_PHOTO_SIZE_MM = 28;

// PDF color palette (Canva-style)
const PDF_COLORS = {
  headerBg: [30, 41, 59],      // slate-800
  accent: [56, 189, 248],       // sky-400
  accentPurple: [167, 139, 250], // violet-400
  textDark: [15, 23, 42],       // slate-900
  textMuted: [71, 85, 105],     // slate-600
  white: [255, 255, 255],
  lightBg: [241, 245, 249],     // slate-100
};

function loadImageAsDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL("image/jpeg", 0.92),
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Image load failed"));
    img.src = url;
  });
}

async function generateAndDownloadCV(profileImageUrl) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const margin = 14;
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - 2 * margin;
  let y = margin;

  const setColor = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  const setFill = (rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);

  const addSection = (title, usePurple = false) => {
    if (y > 255) {
      doc.addPage();
      y = margin;
    }
    const barH = 7;
    setFill(usePurple ? PDF_COLORS.accentPurple : PDF_COLORS.accent);
    doc.rect(margin, y - 4, 4, barH, "F");
    setColor(PDF_COLORS.textDark);
    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text(title, margin + 6, y + 0.5);
    y += 9;
    doc.setFont(undefined, "normal");
    doc.setFontSize(10);
  };

  const addParagraph = (text, indent = 0, color = PDF_COLORS.textDark) => {
    setColor(color);
    const lines = doc.splitTextToSize(text, contentW - indent);
    lines.forEach((line) => {
      if (y > 278) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin + indent, y);
      y += 5;
    });
    y += 2;
  };

  const addBullets = (items) => {
    items.forEach((item) => {
      const bulletLines = doc.splitTextToSize("  • " + item, contentW - 6);
      bulletLines.forEach((line) => {
        if (y > 278) {
          doc.addPage();
          y = margin;
        }
        setColor(PDF_COLORS.textDark);
        doc.text(line, margin + 4, y);
        y += 5;
      });
    });
    y += 2;
  };

  // —— Header block (Canva-style) ——
  const headerH = 38;
  setFill(PDF_COLORS.headerBg);
  doc.rect(0, 0, pageW, headerH, "F");

  let imgData = null;
  let imgW = 1;
  let imgH = 1;
  if (profileImageUrl) {
    try {
      const loaded = await loadImageAsDataUrl(profileImageUrl);
      imgData = loaded.dataUrl;
      imgW = loaded.width;
      imgH = loaded.height;
    } catch (_) {}
  }

  // Preserve aspect ratio: fit within PDF_PHOTO_SIZE_MM so the image isn't stretched
  const maxSize = PDF_PHOTO_SIZE_MM;
  const aspect = imgH > 0 ? imgW / imgH : 1;
  const photoW = aspect >= 1 ? maxSize : maxSize * aspect;
  const photoH = aspect >= 1 ? maxSize / aspect : maxSize;
  const photoX = pageW - margin - photoW;
  const photoY = (headerH - photoH) / 2;

  if (imgData) {
    doc.addImage(imgData, "JPEG", photoX, photoY, photoW, photoH);
    doc.setDrawColor(...PDF_COLORS.accent);
    doc.setLineWidth(0.5);
    doc.rect(photoX, photoY, photoW, photoH);
  }

  setColor(PDF_COLORS.white);
  doc.setFontSize(20);
  doc.setFont(undefined, "bold");
  doc.text(CV_DATA.name, margin, 14);
  setColor(PDF_COLORS.accent);
  doc.setFontSize(11);
  doc.setFont(undefined, "normal");
  doc.text(CV_DATA.title, margin, 20);
  setColor(PDF_COLORS.lightBg);
  doc.setFontSize(9);
  const tagLines = doc.splitTextToSize(CV_DATA.tagline, photoX - margin - 4);
  tagLines.forEach((line, i) => doc.text(line, margin, 26 + i * 4));
  y = headerH + margin;

  // —— About ——
  addSection("About Me");
  addParagraph(CV_DATA.about);

  // —— Skills (tag pills) ——
  addSection("Skills", true);
  const tagH = 6;
  let tagX = margin;
  CV_DATA.skills.forEach((skill) => {
    const tagW = doc.getTextWidth(skill) + 8;
    if (tagX + tagW > pageW - margin) {
      tagX = margin;
      y += tagH + 3;
    }
    setFill(PDF_COLORS.lightBg);
    doc.roundedRect(tagX, y - 3.5, tagW, tagH, 1.5, 1.5, "F");
    setColor(PDF_COLORS.accentPurple);
    doc.setFontSize(8);
    doc.text(skill, tagX + 4, y + 0.5);
    tagX += tagW + 3;
  });
  y += tagH + 8;

  // —— Work Experience ——
  addSection("Work Experience");
  CV_DATA.experience.forEach((job, idx) => {
    if (y > 258) {
      doc.addPage();
      y = margin;
    }
    setColor(PDF_COLORS.accent);
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text(job.title + "  |  " + job.company, margin, y);
    y += 5;
    setColor(PDF_COLORS.textMuted);
    doc.setFont(undefined, "normal");
    doc.setFontSize(9);
    doc.text(job.period, margin, y);
    y += 5;
    addBullets(job.responsibilities);
  });

  // —— Education ——
  addSection("Education", true);
  CV_DATA.education.forEach((edu) => {
    setColor(PDF_COLORS.textDark);
    doc.setFont(undefined, "bold");
    doc.text(edu.institution, margin, y);
    y += 5;
    setColor(PDF_COLORS.textMuted);
    doc.setFont(undefined, "normal");
    doc.text(edu.period, margin, y);
    y += 4;
    addParagraph(edu.description, 0);
  });

  // —— Contact (colored box) ——
  addSection("Contact");
  const contactY = y;
  setFill(PDF_COLORS.lightBg);
  doc.roundedRect(margin, contactY - 3, contentW, 32, 2, 2, "F");
  setColor(PDF_COLORS.textDark);
  doc.setFontSize(9);
  doc.text("Email: " + CV_DATA.contact.email, margin + 5, contactY + 5);
  doc.text("Mobile: " + CV_DATA.contact.mobile, margin + 5, contactY + 11);
  doc.text("Location: " + CV_DATA.contact.location, margin + 5, contactY + 17);
  doc.text("Website: " + CV_DATA.contact.website, margin + 5, contactY + 23);
  y = contactY + 35;

  doc.save("Sam-Yoong-CV.pdf");
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownloadCV = async () => {
    setIsGeneratingPdf(true);
    try {
      await generateAndDownloadCV(samhead);
    } catch (err) {
      console.error(err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   alert("Thank you for your message! I will get back to you soon.");
  //   e.target.reset();
  // };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  const formData = {
    name: e.target.name.value || 'N/A',
    email: e.target.email.value || 'N/A',
    message: e.target.message.value || 'N/A',
  };

  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });

    const data = await response.json();

    if (response.ok) {
      alert("✅ Thank you for your message! I will get back to you soon.");
      e.target.reset();
    } else {
      throw new Error(data.error || 'Failed to send');
    }
  } catch (error) {
    console.error("Error:", error);
    alert("❌ Failed to send message. Please try again later.");
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <div className="App">
      {/* Header */}
      <header className={`header ${isScrolled ? "scrolled" : ""}`}>
        <div className="container nav-container">
          <a href="#home" className="logo">
            Sam<span>Yoong</span>
          </a>
          <div
            className={`hamburger ${isMenuOpen ? "active" : ""}`}
            onClick={toggleMenu}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
          <ul className={`nav-links ${isMenuOpen ? "active" : ""}`}>
            <li>
              <a href="#home" onClick={closeMenu}>
                Home
              </a>
            </li>
            <li>
              <a href="#about" onClick={closeMenu}>
                About
              </a>
            </li>
            <li>
              <a href="#experience" onClick={closeMenu}>
                Experience
              </a>
            </li>
            <li>
              <a href="#education" onClick={closeMenu}>
                Education
              </a>
            </li>
            {/* <li>
              <a href="#projects" onClick={closeMenu}>
                Projects
              </a>
            </li> */}
            <li>
              <a href="#contact" onClick={closeMenu}>
                Contact
              </a>
            </li>
          </ul>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" id="home">
        <div className="container">
          <h1>
            Hi, I'm <span>{CV_DATA.name}</span>
          </h1>
          <p>
            {CV_DATA.title} specializing in{" "}
            <span className="highlight">Mobile Apps</span> and{" "}
            <span className="highlight">Web Applications</span>
          </p>
          <button
            type="button"
            className="btn hero-download-btn"
            onClick={handleDownloadCV}
            disabled={isGeneratingPdf}
          >
            {isGeneratingPdf ? (
              <>
                <span className="spinner" style={{ marginRight: "8px" }}></span>
                Generating...
              </>
            ) : (
              <>
                <i className="fas fa-download" style={{ marginRight: "8px" }}></i>
                Download CV / Resume
              </>
            )}
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about">
        <div className="container">
          <h2>About Me</h2>
          <div className="about-content">
            <div className="about-text">
              <p>{CV_DATA.about}</p>
              <div className="skills">
                {CV_DATA.skills.map((skill) => (
                  <span key={skill} className="skill-tag">{skill}</span>
                ))}
              </div>
            </div>
            <div className="about-image">
              <div className="profile-img card">
                <img src={samhead} alt="Sam Yoong" className="profile-photo" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Work Experience Section */}
      <section id="experience">
        <div className="container">
          <h2>Work Experience</h2>
          <div className="experience-list">
            {CV_DATA.experience.map((job, index) => (
              <ExperienceItem
                key={index}
                title={job.title}
                company={job.company}
                period={job.period}
                responsibilities={job.responsibilities}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education">
        <div className="container">
          <h2>Education</h2>
          <div className="education-list">
            {CV_DATA.education.map((edu, index) => (
              <EducationItem
                key={index}
                institution={edu.institution}
                period={edu.period}
                description={edu.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      {/* <section id="projects">
        <div className="container">
          <h2>My Projects</h2>
          <div className="projects-grid">
            <ProjectCard
              title="ShopEasy Mobile"
              description="A React Native e-commerce application with secure payment integration, product catalog, and user authentication."
              image="Mobile E-commerce App"
            />
            <ProjectCard
              title="TaskFlow"
              description="A React.js-based task management system with drag-and-drop functionality, team collaboration, and real-time updates."
              image="Task Management Web App"
            />
            <ProjectCard
              title="FitTracker"
              description="A cross-platform mobile application for tracking workouts, nutrition, and progress with personalized recommendations."
              image="Fitness Tracking App"
            />
          </div>
        </div>
      </section> */}

      {/* Contact Section */}
      <section id="contact">
        <div className="container">
          <h2>Get In Touch</h2>
          <div className="contact-container">
            <div className="contact-info">
              <ContactItem icon="fas fa-envelope" title="Email" content={CV_DATA.contact.email} />
              <ContactItem icon="fas fa-phone" title="Mobile" content={CV_DATA.contact.mobile} />
              <ContactItem icon="fas fa-map-marker-alt" title="Location" content={CV_DATA.contact.location} />
              <ContactItem icon="fas fa-globe" title="Website" content={CV_DATA.contact.website} />
            </div>
            <form className="contact-form" onSubmit={handleSubmit}>
              {isSubmitting && (
                <div className="form-skeleton">
                  <div className="skeleton-line skeleton-name"></div>
                  <div className="skeleton-line skeleton-email"></div>
                  <div className="skeleton-line skeleton-message"></div>
                  <div className="skeleton-line skeleton-button"></div>
                </div>
              )}
              <div className={`form-content ${isSubmitting ? 'loading' : ''}`}>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name"
                    placeholder="Your Name" 
                    required 
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Your Email"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Your Message"
                    disabled={isSubmitting}
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container">
          <div className="social-links">
            <a
              href="https://github.com/Ypsam"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fab fa-github"></i>
            </a>
            <a
              href="https://www.linkedin.com/in/yp-sam-yoong-567583258/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fab fa-linkedin"></i>
            </a>
            <a
              href="https://t.me/ypsam_yoong"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fab fa-telegram"></i>
            </a>

            {/* <a href="#"><i className="fab fa-twitter"></i></a> */}
            {/* <a href="#"><i className="fab fa-instagram"></i></a> */}
          </div>
          <p className="copyright">© 2025 Sam Yoong. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}

// Contact Item Component
const ContactItem = ({ icon, title, content }) => {
  const isLink = content && (content.startsWith('http://') || content.startsWith('https://'));
  return (
    <div className="contact-item">
      <div className="contact-icon">
        <i className={icon}></i>
      </div>
      <div style={{ width: "100%" }}>
        <h3>{title}</h3>
        {isLink ? (
          <p>
            <a href={content} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>
              {content}
            </a>
          </p>
        ) : (
          <p>{content}</p>
        )}
      </div>
    </div>
  );
};

// Experience Item Component
const ExperienceItem = ({ title, company, period, responsibilities }) => {
  return (
    <div className="experience-item card">
      <div className="experience-header">
        <h3>{title}</h3>
        <h4>{company}</h4>
        <span className="experience-period">{period}</span>
      </div>
      <ul className="experience-responsibilities">
        {responsibilities.map((resp, index) => (
          <li key={index}>{resp}</li>
        ))}
      </ul>
    </div>
  );
};

// Education Item Component
const EducationItem = ({ institution, period, description }) => {
  return (
    <div className="education-item card">
      <div className="education-header">
        <h3>{institution}</h3>
        <span className="education-period">{period}</span>
      </div>
      <p className="education-description">{description}</p>
    </div>
  );
};

export default App;
